import { Client } from "../interfaces/client";
import { Order } from "../interfaces/order";
import { ReferenceDetail } from "../interfaces/referenceDetaill";
import {
  ReferenceOrder,
  SimpleReferenceOrder,
} from "../interfaces/referenceOrder";
import { ROCandidateNotifiedDate } from "../interfaces/roCandidateNotifiedDate";
import { fetchWithAuth } from "./fetchWithAuth";
import { convertBlobToBase64 } from "./helpers";

const APP_URL = process.env.REACT_APP_APP_URL || "";
const DEV_APP_URL = process.env.REACT_APP_DEV_APP_URL || "";
const PROD_APP_URL = process.env.REACT_APP_PROD_APP_URL || "";

const siteBaseUrl =
  window.location.hostname === "localhost"
    ? APP_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_APP_URL
      : DEV_APP_URL;

const API_URL = process.env.REACT_APP_API_URL || "";
const DEV_API_URL = process.env.REACT_APP_DEV_API_URL || "";
const PROD_API_URL = process.env.REACT_APP_PROD_API_URL || "";

const apiBaseUrl =
  window.location.hostname === "localhost"
    ? API_URL
    : window.location.hostname === new URL(PROD_APP_URL).hostname
      ? PROD_API_URL
      : DEV_API_URL;

export const getReferenceOrderByOrderID = async (
  orderID: number,
): Promise<ReferenceOrder> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const referenceOrderResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceOrder/GetReferenceOrderByOrderID/${orderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  //if no referenceOrder, send back a blank
  if (referenceOrderResponse.status === 204) {
    return {
      referenceOrderID: 0,
      orderID,
      years: 0,
      priorClientName: "",
      priorDate: new Date(),
      referenceTypeID: 0,
      invoiceAmount: 0,
      sentToInvoicing: false,
      responseTrackingReceived: "",
      finalReportDate: new Date(),
      finalReportUrl: "",
      invoiceDate: undefined,
      billedDate: undefined,
      adminReviewed: false,
      inAdminReview: false,
      canQCrimAdmit: false,
      retract: false,
    };
  }

  const referenceOrder = await referenceOrderResponse.json();

  return referenceOrder;
};

export const updateReferenceOrder = async (
  referenceOrder: ReferenceOrder,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  try {
    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/ReferenceOrder/UpdateReferenceOrder`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(referenceOrder),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to update the reference order.");
    }
  } catch (error) {
    console.error("Error updating reference order:", error);
    throw error;
  }
};

export const getReferenceOrderByID = async (
  referenceOrderID: number,
): Promise<ReferenceOrder> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const orderResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceOrder/GetReferenceOrderByID/${referenceOrderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!orderResponse.ok) {
    throw new Error("Failed to fetch reference order");
  }

  const order = await orderResponse.json();

  return order;
};

export const getSimpleReferenceOrderByID = async (
  referenceOrderID: number,
): Promise<SimpleReferenceOrder> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const orderResponse = await fetch(
    `${apiBaseUrl}/api/ReferenceOrder/GetSimpleReferenceOrderByID/${referenceOrderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!orderResponse.ok) {
    throw new Error("Failed to fetch reference order");
  }

  const order = await orderResponse.json();

  return order;
};

export const generateReferenceFinalReport = async (
  client: Client,
  order: Order,
  referenceOrder: ReferenceOrder,
  referenceDetails: ReferenceDetail[],
): Promise<Blob | null> => {
  try {
    const formData = {
      client,
      order,
      referenceOrder,
      referenceDetails,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/ReferenceOrder/GenerateReferenceFinalReport`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to generate the reference final report.");
      return null;
    }

    const result = await response.json();

    if (result.success && result.pdfBase64) {
      // Convert Base64 to Blob URL
      const byteCharacters = atob(result.pdfBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      return blob;
    } else {
      console.error("Error from server:", result.message);
      return null;
    }
  } catch (error) {
    console.error("Error generating the reference final report:", error);
    return null;
  }
};

export const sendReferenceFinalReport = async (
  client: Client,
  referenceOrder: ReferenceOrder,
  pdfBlob: Blob,
  byMail: boolean,
): Promise<boolean> => {
  try {
    const finalReportBase64 = await convertBlobToBase64(pdfBlob);

    const formData = {
      client,
      referenceOrder,
      finalReportBase64,
      byMail,
      baseUrl: `${siteBaseUrl}?portal=client`,
    };

    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("User is not authenticated");
    }

    const response = await fetchWithAuth(
      `${apiBaseUrl}/api/ReferenceOrder/SendReferenceFinalReport`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      },
    );

    if (!response.ok) {
      console.error("Failed to send the reference final report.");
      return false;
    }

    if (byMail) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "ReferenceFinalReport.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return true;
    } else {
      const result = await response.json();

      if (result.success) {
        console.log("Final report sent successfully.");
        return true;
      } else {
        console.error("Error from server:", result.message);
        return false;
      }
    }
  } catch (error) {
    console.error("Error sending the reference final report:", error);
    return false;
  }
};

export const getCandidateNotifiedDatesByReferenceOrderID = async (
  referenceOrderID: number,
): Promise<ROCandidateNotifiedDate[]> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const candidateNotifiedDatesResponse = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceOrder/getCandidateNotifiedDatesByReferenceOrderID/${referenceOrderID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!candidateNotifiedDatesResponse.ok) {
    throw new Error("Failed to fetch candidate notified dates");
  }

  const candidateNotifiedDates = await candidateNotifiedDatesResponse.json();

  return candidateNotifiedDates;
};

export const deleteReferenceOrder = async (
  referenceOrderID: number,
): Promise<void> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceOrder/DeleteReferenceOrder/${referenceOrderID}`,
    {
      method: "DELETE",
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error(`Reference Order ID ${referenceOrderID} was not found.`);
    }

    const errorText = await response.text();
    console.error("Delete Reference Detail Error:", errorText);
    throw new Error(
      `Failed to delete reference detail with ID ${referenceOrderID}.`,
    );
  }
};

export const createReferenceOrder = async (
  orderID: number,
): Promise<ReferenceOrder> => {
  if (!apiBaseUrl) {
    throw new Error("API URL is not defined.");
  }

  const token = localStorage.getItem("token");
  if (!token) {
    throw new Error("User is not authenticated.");
  }

  const response = await fetchWithAuth(
    `${apiBaseUrl}/api/ReferenceOrder/CreateReferenceOrder/${orderID}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    },
  );

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Create Reference Order Error:", errorText);
    throw new Error("Failed to create the reference order.");
  }

  const createdOrder: ReferenceOrder = await response.json();
  return createdOrder;
};
